import {
  CLIENT_TICK_MS,
  FIRE_INTERVAL_MS,
  POST_SHOT_SLOWDOWN_MS,
  POST_SHOT_SPEED_DIVISOR,
  SEND_POSITION_INTERVAL_MS,
} from './constants/feel';
import { SpectatorCamera } from './spectator-camera';
import { legacyClientMagnetRadius } from './constants/ships';
import { PLAYER_TARGETED_UPGRADES, upgradeForPath } from './constants/upgrades';
import type { ClientMessage } from '../protocol/gen/warinspace/v2/client_to_server_pb.js';
import type { InputContext, InputSource, Intent, IntentState } from '../input/intent';
import { IDLE_INTENT_STATE } from '../input/intent';
import type { Camera } from '../render/camera';
import type { Renderer } from '../render/renderer';
import { Outbound } from '../net/outbound';
import { COUNTER, PHASE, type FrameProfiler, profiler } from '../debug/profiler';
import { World } from './world';

/** All the loop wants of a `Connection`, so a bench can drive the real loop without a socket. */
export interface LoopConnection {
  readonly isOpen: boolean;
  send(message: ClientMessage): void;
}

/** Wall clock and `requestAnimationFrame` by default; a bench supplies a frame-indexed one so a run is reproducible and is not pinned to the vsync grid. */
export interface FrameDriver {
  now(): number;
  request(callback: (now: number) => void): number;
  cancel(handle: number): void;
}

export const RAF_DRIVER: FrameDriver = {
  now: () => performance.now(),
  request: (callback) => requestAnimationFrame(callback),
  cancel: (handle) => {
    cancelAnimationFrame(handle);
  },
};

export interface LoopOptions {
  readonly world: World;
  readonly connection: LoopConnection;
  readonly renderer: Renderer;
  readonly camera: Camera;
  readonly input: InputSource;
  readonly onFrame?: ((now: number, fps: number) => void) | undefined;
  readonly onToggleUpgradeScreen?: (() => void) | undefined;
  readonly onQuitToMenu?: (() => void) | undefined;
  readonly onMatchOver?: (() => void) | undefined;
  readonly menuOpen?: (() => boolean) | undefined;
  readonly idleMenu?: (() => boolean) | undefined;
  readonly onError: (error: unknown) => void;
  readonly driver?: FrameDriver | undefined;
}

const MAX_CATCHUP_TICKS = 60;

const MENU_FRAME_MS = 1000;
const FPS_WINDOW_MS = 500;

export class GameLoop {
  private running = false;
  private rafHandle = 0;
  private lastTickAt = 0;
  private lastRenderAt = Number.NEGATIVE_INFINITY;
  private framesInWindow = 0;
  private windowStartedAt = 0;
  private fpsValue = 0;
  private lastSendAt = 0;
  private lastFireAt = 0;
  private tickAccumulatorMs = 0;

  private intentState: IntentState = IDLE_INTENT_STATE;

  private shotPending = false;

  private lastShotAt = Number.NEGATIVE_INFINITY;

  private rotationDirty = false;
  private lastSentAngle = Number.NaN;
  private lastSentX = Number.NaN;
  private lastSentY = Number.NaN;

  private readonly driver: FrameDriver;

  readonly spectatorCamera = new SpectatorCamera();
  private wasSpectating = false;

  constructor(private readonly options: LoopOptions) {
    this.driver = options.driver ?? RAF_DRIVER;
  }

  get fps(): number {
    return this.fpsValue;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const now = this.driver.now();
    this.lastTickAt = now;
    this.lastSendAt = now;
    this.windowStartedAt = now;
    this.frame(now);
  }

  stop(): void {
    this.running = false;
    if (this.rafHandle !== 0) {
      this.driver.cancel(this.rafHandle);
      this.rafHandle = 0;
    }
  }

  private readonly frame = (now: number): void => {
    if (!this.running) return;
    const prof = profiler.enabled ? profiler : null;
    prof?.beginFrame(now);

    try {
      this.advance(now, prof);
      if (this.shouldRender(now)) this.render(now, prof);
    } catch (error) {
      this.stop();
      this.options.onError(error);
      return;
    }

    if (prof !== null) {
      this.recordCounts(prof);
      prof.endFrame();
    }

    this.rafHandle = this.driver.request(this.frame);
  };

  private recordCounts(prof: FrameProfiler): void {
    let players = 0;
    let drones = 0;
    let scrap = 0;
    let turrets = 0;
    let controlPoints = 0;
    let other = 0;
    const entities = this.options.world.entities;
    for (const entity of entities.values()) {
      switch (entity.kind) {
        case 'player':
          players += 1;
          break;
        case 'drone':
          drones += 1;
          break;
        case 'scrap':
          scrap += 1;
          break;
        case 'turret':
          turrets += 1;
          break;
        case 'controlPoint':
          controlPoints += 1;
          break;
        default:
          other += 1;
          break;
      }
    }
    prof.count(COUNTER.entities, entities.size);
    prof.count(COUNTER.players, players);
    prof.count(COUNTER.drones, drones);
    prof.count(COUNTER.scrap, scrap);
    prof.count(COUNTER.turrets, turrets);
    prof.count(COUNTER.controlPoints, controlPoints);
    prof.count(COUNTER.otherEntities, other);
  }

  private shouldRender(now: number): boolean {
    const idling =
      (this.options.menuOpen?.() ?? false) &&
      (this.options.idleMenu?.() ?? false) &&
      this.options.renderer.starfieldSettled;
    return !idling || now - this.lastRenderAt >= MENU_FRAME_MS;
  }

  private advance(now: number, prof: FrameProfiler | null): void {
    const { world, camera } = this.options;

    if (world.matchOverDue(now)) this.options.onMatchOver?.();

    this.tickAccumulatorMs += now - this.lastTickAt;
    this.lastTickAt = now;

    const ticks = Math.min(Math.floor(this.tickAccumulatorMs / CLIENT_TICK_MS), MAX_CATCHUP_TICKS);
    if (ticks === MAX_CATCHUP_TICKS) this.tickAccumulatorMs = 0;
    else this.tickAccumulatorMs -= ticks * CLIENT_TICK_MS;

    prof?.begin(PHASE.tick);
    for (let i = 0; i < ticks; i++) this.tick(now);
    prof?.end(PHASE.tick);
    prof?.count(COUNTER.ticks, ticks);

    if (now - this.lastSendAt >= SEND_POSITION_INTERVAL_MS) {
      this.lastSendAt = now;
      prof?.begin(PHASE.send);
      this.send(now);
      prof?.end(PHASE.send);
    }

    prof?.begin(PHASE.interpolate);
    world.interpolate();
    prof?.end(PHASE.interpolate);

    prof?.begin(PHASE.camera);
    if (world.spectating !== this.wasSpectating) {
      this.wasSpectating = world.spectating;
      if (world.spectating) this.spectatorCamera.reset();
      else camera.freeViewDist = null;
    }
    camera.centreX = world.selfX;
    camera.centreY = world.selfY;
    camera.dead = world.dead;
    camera.update();
    prof?.end(PHASE.camera);
  }

  private tick(now: number): void {
    const { world, input, camera } = this.options;

    const context: InputContext = {
      viewportWidth: camera.deviceWidth,
      viewportHeight: camera.deviceHeight,
      worldScale: camera.worldScale,
      cameraX: camera.centreX,
      cameraY: camera.centreY,
      spectating: world.spectating,
      pixelRatio: camera.pixelRatio,
    };

    this.intentState = input.sample(context);
    for (const intent of input.drain()) this.applyIntent(intent, now);

    if (world.spectating) {
      this.spectatorCamera.tick(camera, world, this.intentState.panAxes);
      this.requestMissingNames(now);
      return;
    }

    world.selfAngle = this.intentState.aimAngle;
    this.integrateMovement(now);
    this.collectScrap();
    this.requestMissingNames(now);
  }

  private collectScrap(): void {
    const { world } = this.options;
    if (world.selfTeam === 2) return;
    world.collectScrapInRange(legacyClientMagnetRadius(world.selfShipId));
  }

  private applyIntent(intent: Intent, now: number): void {
    const { world, connection } = this.options;

    switch (intent.kind) {
      case 'fireOnce':
        this.shotPending = true;
        break;

      case 'buyUpgrade':
        connection.send(Outbound.upgrade(intent.path));
        break;

      case 'evolve':
        connection.send(Outbound.evolveShip(intent.path));
        break;

      case 'useAbilityAt': {
        if (!this.abilityReady(now)) break;

        const abilityId = upgradeForPath(world.selfShipId, 0);
        const targetType = PLAYER_TARGETED_UPGRADES.get(abilityId);
        if (targetType !== undefined) {
          const targetId = world.closestPlayerToPoint(intent.worldX, intent.worldY, targetType);
          if (targetId !== null) connection.send(Outbound.useActiveOnPlayer(targetId));
          break;
        }

        connection.send(
          Outbound.useActiveAtPosition(Math.round(intent.worldX), Math.round(intent.worldY)),
        );
        break;
      }

      case 'useAbilityOn':
        if (this.abilityReady(now)) {
          connection.send(Outbound.useActiveOnPlayer(intent.entityId));
        }
        break;

      case 'sendEmoji':
        connection.send(Outbound.sendEmoji(intent.index));
        break;

      case 'suicide':
        if (world.matchInfo !== null) {
          this.options.onQuitToMenu?.();
        } else if (world.playing && !world.dead) {
          connection.send(Outbound.suicide());
        } else if (world.dead) {
          world.returnToMenuRequested = true;
          this.options.onQuitToMenu?.();
        }
        break;

      case 'leaveMatch':
        connection.send(Outbound.notPlayingNextRound());
        break;

      case 'surrenderVote':
        connection.send(Outbound.surrenderVote(intent.agree));
        break;

      case 'sendChat':
        connection.send(Outbound.sendChat(intent.text));
        break;

      case 'zoomView':
        if (world.spectating) {
          this.spectatorCamera.zoomBy(
            intent.steps,
            intent.deviceX,
            intent.deviceY,
            this.options.camera,
            world,
          );
        }
        break;

      case 'pickFollow':
        if (world.spectating) {
          this.spectatorCamera.clickAt(intent.worldX, intent.worldY, this.options.camera, world);
        }
        break;

      case 'seekView':
        if (world.spectating) {
          this.spectatorCamera.seekTo(intent.worldX, intent.worldY, this.options.camera, world);
        }
        break;

      case 'toggleUpgradeScreen':
        this.options.onToggleUpgradeScreen?.();
        break;
    }
  }

  private abilityReady(now: number): boolean {
    const { world } = this.options;
    return world.abilityCooldownMs - (now - world.abilityCooldownSetAt) <= 0;
  }

  private integrateMovement(now: number): void {
    const { world } = this.options;
    if (!world.playing || world.dead) return;

    if (!this.intentState.moving || this.intentState.braking) return;

    const slowed = now - this.lastShotAt < POST_SHOT_SLOWDOWN_MS;
    const speed = slowed ? world.selfMoveSpeed / POST_SHOT_SPEED_DIVISOR : world.selfMoveSpeed;

    const distance = (speed * CLIENT_TICK_MS) / 1000;
    world.selfX += Math.cos(world.selfAngle) * distance;
    world.selfY += Math.sin(world.selfAngle) * distance;
    this.clampToMap();
  }

  private clampToMap(): void {
    const { world } = this.options;
    world.selfX = Math.max(0, Math.min(world.world.maxX, world.selfX));
    world.selfY = Math.max(0, Math.min(world.world.maxY, world.selfY));
  }

  /** Else-if, not a simplification: the legacy send window sends at most one of fire/move/rotation per window, and reproducing that starvation is the point. */
  private send(now: number): void {
    const { world, connection } = this.options;
    if (!connection.isOpen || !world.playing || world.dead) return;

    const x = Math.round(world.selfX);
    const y = Math.round(world.selfY);
    const angle = world.selfAngle;

    if (this.shotPending || this.intentState.firing) {
      if (now - this.lastFireAt >= FIRE_INTERVAL_MS) {
        this.lastFireAt = now;
        this.lastShotAt = now;
        this.shotPending = false;

        const targetId = world.pickFireTarget(now);
        connection.send(
          targetId === null ? Outbound.fireMiss(x, y) : Outbound.fireAtTarget(targetId, x, y),
        );
        world.queueOwnShotSound();
        world.queueOwnShot(targetId, now);
        this.lastSentX = x;
        this.lastSentY = y;
        return;
      }
    }

    if (x !== this.lastSentX || y !== this.lastSentY) {
      this.lastSentX = x;
      this.lastSentY = y;
      if (angle !== this.lastSentAngle) this.rotationDirty = true;
      connection.send(Outbound.moveTo(x, y));
      return;
    }

    if (world.spectating) return;

    if (this.rotationDirty || angle !== this.lastSentAngle) {
      this.lastSentAngle = angle;
      this.rotationDirty = false;
      connection.send(Outbound.setRotation(angle));
    }
  }

  private requestMissingNames(now: number): void {
    const { world, connection } = this.options;
    if (!connection.isOpen) return;
    for (const nameId of world.missingNameIds(now)) {
      connection.send(Outbound.nameRequest(nameId));
    }
  }

  private render(now: number, prof: FrameProfiler | null): void {
    const { renderer, world, camera, onFrame } = this.options;
    this.lastRenderAt = now;
    this.countFrame(now);

    prof?.begin(PHASE.resize);
    renderer.resize(camera);
    prof?.end(PHASE.resize);

    renderer.draw(world, camera, now, this.options.menuOpen?.() ?? false);

    prof?.begin(PHASE.hud);
    onFrame?.(now, this.fpsValue);
    prof?.end(PHASE.hud);
  }

  private countFrame(now: number): void {
    const elapsed = now - this.windowStartedAt;
    if (elapsed >= FPS_WINDOW_MS) {
      this.fpsValue = Math.round((this.framesInWindow * 1000) / elapsed);
      this.framesInWindow = 0;
      this.windowStartedAt = now;
    }
    this.framesInWindow += 1;
  }
}
